import React from 'react';
import './DiagnosticSuggestionsPanel.css';

/**
 * DiagnosticSuggestionsPanel Component
 * 
 * Displays AI-generated diagnostic suggestions with confidence indicators.
 * 
 * 
 * @param {Object} props - Component props
 * @param {string} props.patientId - Unique patient identifier
 * @param {Object} props.diagnosticData - AI diagnostic output data
 * @param {Object} props.findingsData - Raw findings data from annotations
 * @param {Object} props.messagesTemplate - Messages template for localization
 * @param {Object} props.imageInfo - Information about the currently displayed image
 * @param {string} props.loadingState - Loading state
 */
const DiagnosticSuggestionsPanel = ({ patientId, diagnosticData, findingsData, messagesTemplate, imageInfo, loadingState }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  /**
   * Generate diagnostic suggestion text dynamically from findings data
   * @param {Object} findings - Findings data from the backend
   * @param {Object} template - Message template for text generation
   * @param {number} stackIndex - Index of the stack to show findings for
   * @returns {Object} - Generated diagnostic text and metadata
   */
  const generateDiagnosticText = (findings, template, stackId, imageIndex) => {
    if (!findings || !findings.rawData || !template?.assisCovariable?.[0]) {
      return null;
    }

    const t = template.assisCovariable[0];
    const stacks = findings.rawData.stacks || [];

    // Use the provided stack ID and image index to find the corresponding stack data
    const currentStack = stacks.find(stack => stack.stackId === stackId);

    if (!currentStack) {
      return null;
    }

    // Extract findings from current stack only
    const allFindings = [];
    const stack = currentStack;

    console.log('Generating diagnostic text for stack index:', stack);

    // Process only the current stack
    // Check for mass findings (freehand annotations)
    if (stack.freehand && stack.freehand.length > 0) {
      allFindings.push({
        type: 'mass',
        properties: extractMassProperties(stack)
      });
    }

    // Check for calcifications (probe annotations)
    if (stack.probe && stack.probe.length > 0) {
      const distributions = new Set();
      stack.probe.forEach(probe => {
        if (probe.distribution && probe.distribution.length > 0) {
          probe.distribution.forEach(d => distributions.add(d));
        }
      });

      if (distributions.size > 0) {
        allFindings.push({
          type: 'calcifications',
          properties: {
            distribution: Array.from(distributions)
          }
        });
      }
    }

    if (allFindings.length === 0) {
      return null;
    }

    // Build diagnostic text
    let text = t.covariable_001; // "In this case, we have "

    // Number of findings
    text += allFindings.length;
    text += allFindings.length === 1 ? t.covariable_002[0] : t.covariable_002[1];

    // Describe each finding
    allFindings.forEach((finding, index) => {
      if (index === 0) {
        text += t.covariable_003[0][allFindings.length > 1 ? 1 : 0]; // " One finding " or " The finding "
      } else if (index === 1) {
        text += t.covariable_003[1]; // ". The second finding "
      } else if (index === 2) {
        text += t.covariable_003[2]; // ". The third finding "
      }

      text += allFindings.length > 1 ? t.covariable_004[1] : t.covariable_004[0]; // "are " or "is "

      // Finding type
      if (finding.type === 'mass') {
        text += t.covariable_005[0]; // "a mass"
      } else if (finding.type === 'calcifications') {
        text += t.covariable_005[1]; // "calcifications"
      }

      // Properties
      if (finding.properties) {
        const props = [];

        if (finding.properties.shape) {
          props.push(finding.properties.shape + t.covariable_007[0]); // " shape"
        }
        if (finding.properties.margin) {
          props.push(finding.properties.margin + t.covariable_007[1]); // " margin"
        }
        if (finding.properties.density) {
          props.push(finding.properties.density + t.covariable_007[2]); // " density"
        }
        if (finding.properties.distribution && finding.properties.distribution.length > 0) {
          props.push(finding.properties.distribution[0] + t.covariable_007[3]); // " distribution"
        }

        if (props.length > 0) {
          text += t.covariable_006; // " with "
          props.forEach((prop, i) => {
            if (i > 0) {
              text += i === props.length - 1 ? t.covariable_008[1] : t.covariable_008[0]; // " and " or ", "
            }
            text += prop;
          });
        }
      }
    });

    // Add family/personal history statement
    text += t.covariable_017; // ". There are "
    text += t.covariable_012; // "no "
    text += t.covariable_018; // "findings"
    text += t.covariable_019; // " regarding "
    text += t.covariable_010; // "family history"
    text += t.covariable_013; // " or "
    text += t.covariable_011; // "personal history"
    text += ".";

    // Update the output file in backend with the generated text for this stack
    // This allows the text to be stored for future reference
    fetch(`${BACKEND_URL}/api/assistant/outputs/${patientId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ patient: [{ diagnosticTextAssis: text }] })
    })
      .then(data => {
        console.log('Diagnostic text updated successfully:', data);
      })
      .catch(error => {
        console.error('Error updating diagnostic text:', error);
      });

    return text;
  };

  /**
   * Extract mass properties from stack data
   * @param {Object} stack - Stack data
   * @returns {Object} - Mass properties
   */
  const extractMassProperties = (stack) => {
    // This is a simplified version - in a real implementation,
    // you would extract actual properties from the annotation data
    return {
      shape: 'oval',
      margin: 'microlobulated',
      density: 'high'
    };
  };

  const getBiradsLevel = (value) => {
    const normalizedValue = String(value ?? '').trim().toLowerCase();

    if (normalizedValue.includes('low') || ['1', '2', '3'].includes(normalizedValue)) {
      return 'Low';
    }

    if (normalizedValue.includes('moderate') || normalizedValue === '4') {
      return 'Moderate';
    }

    if (normalizedValue.includes('high') || ['5', '6'].includes(normalizedValue)) {
      return 'High';
    }

    return '';
  };

  // Direct color mapping for BI-RADS levels
  const getBiradsColor = (value) => {
    const level = getBiradsLevel(value);

    if (level === 'Low') {
      return [0, 158, 15];
    }

    if (level === 'Moderate') {
      return [255, 153, 0];
    }

    if (level === 'High') {
      return [207, 42, 39];
    }

    return [0, 158, 15];
  };

  const getBiradsSuspicionLabel = (value) => {
    return getBiradsLevel(value) || value || 'Unknown';
  };

  // Loading state
  if (loadingState === 'loading') {
    return (
      <div className="diagnostic-panel">
        <div className="panel-header">
          <h4>Diagnostic Suggestions</h4>
        </div>
        <div className="panel-content loading">
          <div className="loading-spinner"></div>
          <p>Analyzing diagnostic data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (loadingState === 'error') {
    return (
      <div className="diagnostic-panel">
        <div className="panel-header">
          <h4>Diagnostic Suggestions</h4>
        </div>
        <div className="panel-content error">
          <p>Diagnostic data unavailable</p>
        </div>
      </div>
    );
  }

  // No patient selected
  if (loadingState === 'no-patient') {
    return (
      <div className="diagnostic-panel">
        <div className="panel-header">
          <h4>Diagnostic Suggestions</h4>
        </div>
        <div className="panel-content empty">
          <p>Select a patient to view diagnostic suggestions</p>
        </div>
      </div>
    );
  }

  // Extract patient data
  const patientInfo = diagnosticData?.patient?.[0];

  if (!patientInfo) {
    return (
      <div className="diagnostic-panel">
        <div className="panel-header">
          <h4>Diagnostic Suggestions</h4>
        </div>
        <div className="panel-content empty">
          <p>No diagnostic suggestions available for this patient</p>
        </div>
      </div>
    );
  }

  const biradsSource = patientInfo?.biradsLabelPhys || patientInfo?.biradsLabelAssis || patientInfo?.biradsPhys || patientInfo?.biradsAssis;
  const biradsColor = getBiradsColor(biradsSource);
  const biradsLabel = getBiradsSuspicionLabel(biradsSource);

  // Generate diagnostic suggestion text for current stack
  const diagnosticResult = patientInfo?.diagnosticTextPhys
    || patientInfo?.diagnosticTextAssis
    || generateDiagnosticText(findingsData, messagesTemplate, imageInfo?.stackId, imageInfo?.imageIndex);

  return (
    <div className="diagnostic-panel">
      <div className="panel-header">
        <h4>Diagnostic Suggestions</h4>
      </div>

      <div className="panel-content">
        {/* Primary Diagnostic Metrics */}
        <div className="diagnostic-section primary-metrics">
          {/* Cancer Probability */}
          {/*
          <div className="metric-card">
            <div className="metric-header">
              <div className="metric-label">Cancer Probability</div>
            </div>
            <div className="metric-value">
              <span 
                className="value-text large"
                style={{ color: `rgb(${probabilityColor[0]}, ${probabilityColor[1]}, ${probabilityColor[2]})` }}
              >
                {patientInfo.cancerChance}%
              </span>
            </div>
          </div>
          */}

          {/* BI-RADS Classification */}
          <div className="metric-card">
            <div className="metric-header">
              <div className="metric-label">Suspicion Level</div>
            </div>
            <div className="metric-value">
              <span
                className="value-text large birads"
                style={{ color: `rgb(${biradsColor[0]}, ${biradsColor[1]}, ${biradsColor[2]})` }}
              >
                {biradsLabel}
              </span>
            </div>
          </div>
        </div>

        {/* Diagnostic Text Summary */}
        {diagnosticResult && (
          <div className="diagnostic-section text-summary">
            <div className="section-header">
              <h5>Clinical Findings</h5>
            </div>
            <div className="summary-text">
              <p>{diagnosticResult}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiagnosticSuggestionsPanel;
